import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import _ from "lodash";
import { RootState } from "../configureStore";
import {
    fetchPermissions as apiFetchPermissions,
    fetchResources as apiFetchResources,
    fetchUserDetails as apiFetchUserDetails,
    ModulePermission,
    Permissions,
    postUserDetails as apiPostUserDetails, Resources,
    UserDetails
} from "./api";
import {Permission } from '../containers/CohortBuilder/PermissionsUtil'

interface UserState {
    permissions: Permissions | undefined
    resources: Resources | undefined
    user: UserDetails | undefined
}

const initialState: UserState = {
    permissions: undefined,
    resources: undefined,
    user: undefined
}

export const fetchPermissions = createAsyncThunk(
    'user/fetchPermissions',
    async () => {
        return apiFetchPermissions()
    }
)

export const fetchResources = createAsyncThunk(
    'user/fetchResources',
    async () => {
        return apiFetchResources()
    }
)

export const fetchUser = createAsyncThunk(
    'user/fetchUser',
    async (_, {rejectWithValue}) => {
        try {
            return apiFetchUserDetails()
        } catch (e) {
            return rejectWithValue(undefined)
        }
    }
)

export const updateUser = createAsyncThunk(
    'user/updateUser',
    async (userDetails: UserDetails) => {
        return apiPostUserDetails(userDetails)
    }
)

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(fetchPermissions.pending, (state) => {
            if (!_.isNil(state.permissions)) {
                state.permissions = undefined
            }
        })
        builder.addCase(fetchPermissions.fulfilled, (state: UserState, action) => {
            state.permissions = action.payload
        })
        builder.addCase(fetchResources.pending, (state) => {
            if (!_.isNil(state.permissions)) {
                state.resources = undefined
            }
        })
        builder.addCase(fetchResources.fulfilled, (state: UserState, action) => {
            state.resources = action.payload
        })
        builder.addCase(fetchUser.pending, (state) => {
            if (!_.isNil(state.user)) {
                state.user = undefined
            }
        })
        builder.addCase(fetchUser.fulfilled, (state: UserState, action) => {
            state.user = action.payload
        })
        builder.addCase(updateUser.fulfilled, (state: UserState, action) => {
            state.user = action.payload
        })
    },
})

export const selectPermissions = (state: RootState) => state.user.permissions;
export const selectResources = (state: RootState): Resources | undefined => state.user.resources;
export const selectUser = (state: RootState) => state.user.user

const hasPermission = (permission: ModulePermission, state: RootState): boolean => {
    return state.user.permissions !== undefined && state.user.permissions.modulePermissions.includes(permission)
}

const hasQueryHistoryPermission = (permission: ModulePermission, state: RootState): boolean => {
    let vantageUser = selectUser(state)
    let hasAdminPermission = !!vantageUser?.role.includes('ADMIN')
    let hasTeamPermission = hasPermission(permission, state)
    return hasAdminPermission || hasTeamPermission
}

export const hasCBPermission = (state: RootState) => hasPermission(ModulePermission.COHORT_BUILDER, state)
export const hasHRUPermission = (state: RootState) => hasPermission(ModulePermission.COHORT_HRU, state)
export const hasPWVPermission = (state: RootState) => hasPermission(ModulePermission.COHORT_PWV, state)
export const hasQBPermission = (state: RootState) => hasPermission(ModulePermission.QUERY_BUILDER, state)
export const hasStoryboardPermission = (state: RootState) => hasPermission(ModulePermission.STORYBOARD, state)
export const hasStoryboardEditPermission = (state: RootState) => hasPermission(ModulePermission.STORYBOARD_EDIT, state)
export const hasRetinalMarketTrends = (state: RootState) => hasPermission(ModulePermission.STORYBOARD_RETINA_MARKET_TRENDS, state)
export const hasVestrumPracticePerformanceTracker = (state: RootState) => hasPermission(ModulePermission.STORYBOARD_VESTRUM_PRACTICE_PERFORMANCE_TRACKER, state)
export const hasCompanyQueryHistoryViewPermission = (state: RootState) => hasQueryHistoryPermission(ModulePermission.COMPANY_QUERY_HISTORY_VIEW, state)
export const getPermissions = (state: RootState): Permission => {
    return {
        cb: hasCBPermission(state),
        hru: hasHRUPermission(state),
        pwv: hasPWVPermission(state),
        qb: hasQBPermission(state),
        companyQueryHistory: hasCompanyQueryHistoryViewPermission(state),
        owner: state.current.study?.loginId === state.user.user?.username,
        example: state.current.study?.example,
        shared: false // TODO: implement
    }
}

export default userSlice.reducer